import { ArrowRightIcon, XIcon } from 'lucide-react';
import { ComponentProps, useState } from 'react';
import { Link, type To } from 'react-router';

import { Button } from '@/components/core/button';
import { DEFAULT_PAGE_TITLE } from '@/constants';
import { cn } from '@/shared/helpers';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';

import { useWizardContext } from './wizard-context';

export function WizardHeader({ children, className, ...props }: ComponentProps<'header'>) {
  return (
    <header
      className={cn(
        'grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-3 border-b px-(--page-inset) py-3 sm:h-14 sm:grid-cols-[auto_minmax(0,1fr)_auto_minmax(0,1fr)]',
        className,
      )}
      {...props}
    >
      {children}
    </header>
  );
}

export function WizardCancel({ to, className }: { to: To; className?: string }) {
  return (
    <Button
      asChild
      variant="ghost"
      size="icon"
      className={cn('col-start-1 row-start-1 -ml-2 justify-self-start', className)}
    >
      <Link to={to} aria-label="Cancel">
        <XIcon className="size-5" />
      </Link>
    </Button>
  );
}

export function WizardTitle({
  pageTitle,
  children,
  className,
  ...props
}: ComponentProps<'h1'> & {
  pageTitle?: string;
}) {
  useDocumentTitle(pageTitle ?? (typeof children === 'string' ? children : DEFAULT_PAGE_TITLE));
  return (
    <h1
      className={cn('col-start-2 row-start-1 min-w-0 truncate text-base font-bold', className)}
      {...props}
    >
      {children}
    </h1>
  );
}

export function WizardActions({
  submitLabel,
  onSubmit,
  className,
}: {
  submitLabel: string;
  onSubmit: () => void | Promise<void>;
  className?: string;
}) {
  const { areRequiredStepsComplete, attemptAllSteps, attemptCurrentStep, isCurrentStepLast } =
    useWizardContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!areRequiredStepsComplete) return;

    setIsSubmitting(true);
    try {
      const valid = await attemptAllSteps();
      if (valid) {
        await onSubmit();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (isCurrentStepLast) return;

    await attemptCurrentStep();
  };

  return (
    <div
      className={cn(
        'col-start-3 row-start-1 flex gap-2 justify-self-end sm:col-start-4',
        className,
      )}
    >
      <Button
        type="button"
        loading={isSubmitting}
        disabled={!areRequiredStepsComplete}
        onClick={handleSubmit}
      >
        {submitLabel}
      </Button>
      <Button type="button" disabled={isCurrentStepLast || isSubmitting} onClick={handleNext}>
        Next <ArrowRightIcon />
      </Button>
    </div>
  );
}
